import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { useLang } from '../contexts/LangContext';
import { Trash, Pencil, Check, ShareNetwork, CheckCircle, X, SealCheck, DeviceMobile, Desktop, Textbox, FolderSimple } from '@phosphor-icons/react';
import { TranslatorBox, TranslateWord } from '../components/TranslateWidgets';
import { VocabShareInbox, SendCardsButton } from '../components/VocabSharing';
import { AIFlashcardGeneratorButton } from '../components/AIFlashcardGenerator';
import { FlashcardReviewButton } from '../components/FlashcardReview';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function VocabularyPage() {
  const { user } = useAuth();
  const { lang } = useLang();
  const isDe = lang === 'de';
  const isTeacher = user?.role === 'admin' || user?.role === 'teacher';
  const [scope, setScope] = useState('mine'); // mine | level | pending(teacher)
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editBuf, setEditBuf] = useState({});
  const [folders, setFolders] = useState([]);
  const [folder, setFolder] = useState('');
  const folderScope = scope === 'mine' || scope === 'learned';

  const loadFolders = async () => {
    try {
      const res = await axios.get(`${API}/flashcards/folders`, { withCredentials: true });
      setFolders(res.data || []);
    } catch { /* non-critical */ }
  };
  const load = async (s = scope) => {
    setLoading(true);
    try {
      const f = folderScope && folder ? `&folder=${encodeURIComponent(folder)}` : '';
      const res = await axios.get(`${API}/flashcards?scope=${s}${f}`, { withCredentials: true });
      setCards(res.data || []);
    } catch { toast.error('Fehler'); }
    finally { setLoading(false); }
    loadFolders();
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [scope, folder]);
  useEffect(() => { if (folder && !folders.some(f => f.name === folder)) setFolder(''); }, [folders, folder]);

  const remove = async (id) => {
    if (!window.confirm('¿Eliminar tarjeta?')) return;
    try { await axios.delete(`${API}/flashcards/${id}`, { withCredentials: true }); toast.success('Eliminada'); load(); }
    catch { toast.error('Fehler'); }
  };
  const saveEdit = async () => {
    try {
      await axios.patch(`${API}/flashcards/${editing}`, editBuf, { withCredentials: true });
      toast.success('Guardada');
      setEditing(null); load();
    } catch { toast.error('Fehler'); }
  };
  const submitForReview = async (id) => {
    try {
      await axios.patch(`${API}/flashcards/${id}`, { submitted_for_review: true }, { withCredentials: true });
      toast.success('Enviada al profesor');
      load();
    } catch { toast.error('Fehler'); }
  };
  const toggleApprove = async (id, current) => {
    try {
      await axios.patch(`${API}/flashcards/${id}`, { approved: !current }, { withCredentials: true });
      load();
    } catch { toast.error('Fehler'); }
  };

  const toggleLearned = async (id, current) => {
    try {
      await axios.patch(`${API}/flashcards/${id}`, { learned: !current }, { withCredentials: true });
      toast.success(current ? 'Movida a "Mis tarjetas"' : '¡Aprendida! 🎉');
      load();
    } catch { toast.error('Fehler'); }
  };

  return (
    <div className="space-y-5" data-testid="vocab-page">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold text-[#292524] flex items-center gap-2">
          <Textbox size={28} weight="duotone" className="text-[#5B8C9A]" />
          {isDe ? 'Vokabeln' : 'Vocabulario'}
        </h1>
        <p className="text-sm text-[#57534E] mt-1">
          {isTeacher
            ? 'Verwalte Karten aller Schüler. Genehmige Einreichungen, damit sie für das ganze Niveau sichtbar werden.'
            : 'Crea tus propias tarjetas, mándalas a revisión y revisa las aprobadas por tu profesor.'}
        </p>
      </div>

      <AIFlashcardGeneratorButton folders={folders} onSaved={() => load()} />

      <TranslatorBox onSaved={() => load()} />

      {/* Peer-to-peer sharing inbox — students see incoming card bundles
          from buddies and pick which ones to add to their own deck.
          Sender gets +1 point per accepted card. */}
      {!isTeacher && <VocabShareInbox onAccepted={() => load()} />}

      {/* How-to hint — compact single-line banner outside the cards box so it's
          visible even when tarjetas already exist. */}
      <div
        className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-xs text-[#78716C] bg-stone-50 border border-stone-200 rounded-lg px-3 py-2"
        data-testid="vocab-howto-hint"
      >
        <span className="inline-flex items-center gap-1.5">
          <DeviceMobile size={14} weight="duotone" className="text-[#5B8C9A]" />
          <strong className="text-[#57534E]">{isDe ? 'Handy:' : 'Móvil:'}</strong>
          {isDe ? 'Wort gedrückt halten' : 'Mantén pulsado el dedo'}
        </span>
        <span className="inline-flex items-center gap-1.5">
          <Desktop size={14} weight="duotone" className="text-[#AB1C24]" />
          <strong className="text-[#57534E]">PC:</strong>
          {isDe ? 'Doppelklick' : 'Doble click'}
        </span>
      </div>

      {/* Scope tabs */}
      <div className="flex gap-2 flex-wrap">
        {[
          { v: 'mine', label: 'Mis tarjetas' },
          ...(!isTeacher ? [{ v: 'learned', label: 'Aprendidas' }] : []),
          { v: 'level', label: isTeacher ? 'Todas' : 'Nivel · Aprobadas' },
          ...(isTeacher ? [{ v: 'pending', label: 'Por aprobar' }] : []),
        ].map(tab => (
          <button
            key={tab.v}
            onClick={() => setScope(tab.v)}
            className={`px-4 py-2 rounded-full text-sm font-semibold ${scope === tab.v ? 'bg-[#AB1C24] text-white' : 'bg-stone-100 text-stone-700 hover:bg-stone-200'}`}
            data-testid={`vocab-tab-${tab.v}`}
          >{tab.label}</button>
        ))}
      </div>

      {scope === 'mine' && (cards.length > 0 || folder) && (
        <div className="flex gap-2 flex-wrap items-center" data-testid="vocab-folder-actions">
          <FlashcardReviewButton cards={cards} folder={folder} isDe={isDe} onDone={() => load()} />
          {folder && !isTeacher && <SendCardsButton folder={folder} />}
        </div>
      )}

      {folderScope && folders.length > 0 && (
        <div className="flex gap-2 flex-wrap items-center" data-testid="vocab-folder-filter">
          <FolderSimple size={18} weight="duotone" className="text-[#5B8C9A]" />
          {[{ name: '', label: isDe ? 'Alle Ordner' : 'Todas las carpetas' }, ...folders.map(f => ({ name: f.name, label: `${f.name} · ${f.count}` }))].map(f => (
            <button
              key={f.name || 'all'}
              onClick={() => setFolder(f.name)}
              className={`px-3 py-1 rounded-full text-xs font-semibold border transition-colors ${folder === f.name ? 'bg-[#5B8C9A] border-[#5B8C9A] text-white' : 'bg-white border-stone-200 text-stone-700 hover:bg-stone-50'}`}
              data-testid={`vocab-folder-${f.name || 'all'}`}
            >{f.label}</button>
          ))}
        </div>
      )}

      {loading ? (
        <p className="text-center text-[#78716C] py-6">Cargando…</p>
      ) : cards.length === 0 ? (
        <div
          className="text-center py-10 bg-white rounded-xl border border-stone-200"
          data-testid="vocab-empty"
        >
          <p className="text-[#78716C]">
            {isDe ? 'Noch keine Karten.' : 'No hay tarjetas todavía.'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {cards.map(c => {
            const isOwner = c.user_id === user?.user_id;
            const isEditing = editing === c.card_id;
            return (
              <div key={c.card_id} className={`relative bg-white rounded-xl border p-4 ${c.approved ? 'border-emerald-200' : 'border-stone-200'}`} data-testid={`card-${c.card_id}`}>
                {c.approved && (
                  <span className="absolute top-2 right-2 inline-flex items-center gap-1 text-[10px] font-bold bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full">
                    <CheckCircle size={12} weight="fill" /> Aprobada
                  </span>
                )}
                {isEditing ? (
                  <div className="space-y-2">
                    <input className="w-full border border-stone-300 rounded px-2 py-1 text-sm font-semibold" value={editBuf.word || ''}
                      onChange={e => setEditBuf(p => ({ ...p, word: e.target.value }))} placeholder="Palabra" />
                    <input className="w-full border border-stone-300 rounded px-2 py-1 text-sm" value={editBuf.translation || ''}
                      onChange={e => setEditBuf(p => ({ ...p, translation: e.target.value }))} placeholder="Traducción" />
                    <textarea className="w-full border border-stone-300 rounded px-2 py-1 text-xs" rows={2} value={editBuf.example_sentence || ''}
                      onChange={e => setEditBuf(p => ({ ...p, example_sentence: e.target.value }))} placeholder="Ejemplo (opcional)" />
                    {isOwner && (
                      <input list="vocab-folder-options" className="w-full border border-stone-300 rounded px-2 py-1 text-xs" value={editBuf.folder || ''}
                        onChange={e => setEditBuf(p => ({ ...p, folder: e.target.value }))} placeholder={isDe ? 'Ordner (optional)' : 'Carpeta (opcional)'} data-testid="card-folder-input" />
                    )}
                    <div className="flex gap-2">
                      <button onClick={saveEdit} className="flex-1 bg-[#AB1C24] text-white rounded py-1 text-xs font-semibold hover:bg-[#8A151C]" data-testid="card-save"><Check size={14} className="inline" /> Guardar</button>
                      <button onClick={() => setEditing(null)} className="flex-1 bg-stone-200 rounded py-1 text-xs"><X size={14} className="inline" /> Cancelar</button>
                    </div>
                  </div>
                ) : (
                  <>
                    <p className="text-xs text-[#AB1C24] uppercase tracking-wider font-bold flex items-center gap-2 pr-16">
                      {c.direction?.replace('->', ' → ')}
                      {isOwner && c.folder && (
                        <span className="normal-case tracking-normal font-semibold text-[10px] text-[#5B8C9A] bg-[#5B8C9A]/10 px-1.5 py-0.5 rounded inline-flex items-center gap-1 truncate" data-testid={`card-folder-${c.card_id}`}>
                          <FolderSimple size={10} weight="fill" />{c.folder}
                        </span>
                      )}
                    </p>
                    <p className="font-bold text-[#292524] mt-1 text-lg">
                      <TranslateWord>{c.word}</TranslateWord>
                    </p>
                    <p className="text-sm text-[#292524]">→ {c.translation}</p>
                    {c.example_sentence && <p className="text-xs text-[#78716C] italic mt-1">"{c.example_sentence}"</p>}
                    {!isOwner && c.user_name && <p className="text-[10px] text-[#A8A29E] mt-2">— {c.user_name} · {c.level}</p>}
                    <div className="flex items-center gap-1 mt-3 flex-wrap">
                      {isOwner && (
                        <button
                          onClick={() => toggleLearned(c.card_id, c.learned)}
                          title={c.learned ? 'Marcar como no aprendida' : '¡Ya la sé! Mover a Aprendidas'}
                          className={`text-xs px-3 py-1.5 rounded-full font-bold flex items-center gap-1 transition ${
                            c.learned
                              ? 'bg-emerald-600 text-white hover:bg-emerald-700'
                              : 'bg-emerald-50 text-emerald-700 border border-emerald-300 hover:bg-emerald-100'
                          }`}
                          data-testid={`card-learned-${c.card_id}`}
                        >
                          <SealCheck size={14} weight={c.learned ? 'fill' : 'bold'} />
                          {c.learned ? 'Aprendida' : 'Ya la sé'}
                        </button>
                      )}
                      {(isOwner || isTeacher) && (
                        <button onClick={() => { setEditing(c.card_id); setEditBuf(c); }} className="text-xs px-2 py-1 bg-stone-100 rounded hover:bg-stone-200" data-testid={`card-edit-${c.card_id}`}><Pencil size={12} className="inline" /></button>
                      )}
                      {(isOwner || isTeacher) && (
                        <button onClick={() => remove(c.card_id)} className="text-xs px-2 py-1 bg-stone-100 rounded hover:bg-red-100 text-red-600" data-testid={`card-del-${c.card_id}`}><Trash size={12} className="inline" /></button>
                      )}
                      {isOwner && !isTeacher && (
                        <SendCardsButton card={c} />
                      )}
                      {isOwner && !c.approved && !c.submitted_for_review && (
                        <button onClick={() => submitForReview(c.card_id)} className="text-xs px-2 py-1 bg-amber-100 text-amber-700 rounded hover:bg-amber-200" data-testid={`card-submit-${c.card_id}`}><ShareNetwork size={12} className="inline" /> Enviar a revisión</button>
                      )}
                      {isOwner && c.submitted_for_review && !c.approved && (
                        <span className="text-xs px-2 py-1 bg-amber-50 text-amber-700 rounded">En revisión</span>
                      )}
                      {isTeacher && (
                        <button onClick={() => toggleApprove(c.card_id, c.approved)} className={`text-xs px-2 py-1 rounded font-semibold ${c.approved ? 'bg-emerald-600 text-white' : 'bg-stone-200 text-stone-700 hover:bg-emerald-100'}`} data-testid={`card-toggle-approve-${c.card_id}`}>
                          {c.approved ? '✓ Aprobada' : 'Aprobar'}
                        </button>
                      )}
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}
      <datalist id="vocab-folder-options">{folders.map(f => <option key={f.name} value={f.name} />)}</datalist>
    </div>
  );
}
